import React from "react";
import { Trans } from "@lingui/macro";
import Modal from "components/Modal/Modal";
import { ModalProps } from "../Modal/Modal";
import {
  ResponsiveSanityImage,
  SanityImageRatio
} from "@silversea-ssc/ui-components-react";
import ExcursionActivityLevel from "components/ExcursionActivityLevel/ExcursionActivityLevel";
import ExcursionAccessibilityLevel from "components/ExcursionAccessibilityLevel/ExcursionAccessibilityLevel";
import DefaultShorexIcon from "components/ShorexIcon/DefaultShorexIcon";
import ShorexIcon from "components/ShorexIcon/ShorexIcon";
import { FetchApiStatus } from "../../utils/useFetchApi";
import * as styles from "./PortExcursionModal.module.scss";
import { formatNumber } from "src/utils/localization/formatNumber";
import { useLanguage } from "../../utils/localization/languages/I18nContext";
import { IconName, ModalBody } from "@silversea-ssc/ui-components-react";

export interface PortExcursionModalProps extends ModalProps {
  _key?: string;
  activityLevel?: number;
  accessibility?: string;
  code?: string;
  features?: IconName[];
  name?: string;
  descriptionHtml?: string;
  noteHtml?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  image?: any;
  status: FetchApiStatus;
  hasIcons?: boolean;
  duration?: number;
  hideDurationAndCode?: boolean;
}

const PortExcursionModal: React.FC<PortExcursionModalProps> = ({
  modal,
  activityLevel,
  accessibility,
  features,
  name,
  descriptionHtml,
  noteHtml,
  image,
  status,
  hasIcons = true,
  code,
  duration,
  hideDurationAndCode = false
}) => {
  const language = useLanguage();
  return (
    <Modal
      title={name}
      modal={modal}
      size="small"
      data-content-name={`modal_excursion_${code}`}
    >
      <ModalBody variant="wide" className={styles.body}>
        {status == "loading" && <span>loading...</span>}
        {status == "success" && (
          <React.Fragment>
            {image && (
              <SanityImageRatio ratio="auto" image={image}>
                <ResponsiveSanityImage
                  image={image}
                  defaultWidth={800}
                  defaultHeight={450}
                  className={styles.image}
                  fit="original"
                  //imgStyle={{ objectFit: "contain" }}
                />
              </SanityImageRatio>
            )}
            {hasIcons && (
              <div className={styles.icons}>
                <div className={styles.iconsRow}>
                  {activityLevel && (
                    <ExcursionActivityLevel
                      level={activityLevel}
                      titleClassName={styles.activityLevelAlignFix}
                    />
                  )}

                  {!hideDurationAndCode && duration && duration > 0 && (
                    <React.Fragment>
                      <ShorexIcon
                        key="icon-duration"
                        iconName="duration"
                        showDescription
                        size="big"
                        label={"Duration"}
                        description={
                          <React.Fragment>
                            <Trans>Duration</Trans>:{" "}
                            <strong>
                              {formatNumber(
                                duration,
                                {
                                  style: "decimal"
                                },
                                language.code
                              )}{" "}
                              <Trans>hours</Trans>
                            </strong>
                          </React.Fragment>
                        }
                      />
                    </React.Fragment>
                  )}

                  {features?.map((i, index) => (
                    <DefaultShorexIcon
                      key={index}
                      iconName={i}
                      showDescription
                      size="big"
                    />
                  ))}
                </div>
                {accessibility && (
                  <ExcursionAccessibilityLevel
                    level={accessibility}
                    showDescription
                    size="big"
                  />
                )}
              </div>
            )}

            {descriptionHtml && (
              <div
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                className={styles.content}
              />
            )}
            {noteHtml && (
              <div
                dangerouslySetInnerHTML={{ __html: noteHtml }}
                className={styles.content}
              />
            )}
          </React.Fragment>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PortExcursionModal;
